import {createSelector} from '@reduxjs/toolkit'

import {isEmpty} from '@shared/helpers/lang'

import {
    InterestFreeInstallmentType,
    InterestFreeInstallmentTypeType,
    MaximumUsingChargePointAmount,
    NormalPayMethods,
    PaymentMethodStatus,
    PaymentMethodStatusType,
    PaymentTabMethod,
    PaymentTabMethodType,
    PayMethod,
    PayMethodType,
} from '@web/core/constants'
import {accountSelector, bankEasyPayAccountInfoSelector, rechargeAccountInfoSelector} from '@web/slices/account/slice'
import {cardEasyPayInfoSelector, cardPromotionInfoSelector, cardSelector} from '@web/slices/card/slice'
import {chargePointSelector, payMoneyInfoSelector} from '@web/slices/chargePoint/slice'
import {commonSelector, pageSetInfoSelector} from '@web/slices/common/slice'
import {mobileSelector} from '@web/slices/mobile/slice'
import {moneyTokenSelector} from '@web/slices/moneyToken/slice'
import {paymentSelector, payMethodAvailableSelector, payMethodInfoSelector} from '@web/slices/paymentMethod/slice'
import {payPalSelector} from '@web/slices/payPal/slice'
import {voucherSelector} from '@web/slices/voucher/slice'

import {selectedInstallmentInfoSelector} from './card'
import {familyPayStatementSelector} from './familyPay'
import {paymentAmountInfoSelectors} from './paymentAmount'
import {payMoneyDisabledSelector, rechargePaymentInfoSelector} from './rechargePaymethod'

/** 결제수단 info selector */
export const paymentMethodInfoSelector = createSelector(
    paymentAmountInfoSelectors,
    chargePointSelector,
    payMoneyInfoSelector,
    paymentSelector,
    rechargePaymentInfoSelector,
    payMethodInfoSelector,
    cardPromotionInfoSelector,
    (
        {paymentAmount, usingPayMethodAmount},
        {amount: chargePointAmount, recharging: {amount: rechargePointAmount}},
        {isUsedLikePayMoney},
        {selected},
        {complex},
        payMethodInfo,
        {selected: selectedCardPromotionGroup},
    ) => {
        const isComplex =
            complex.available &&
            paymentAmount >
                Math.min(
                    rechargePointAmount + (isUsedLikePayMoney ? chargePointAmount : 0),
                    MaximumUsingChargePointAmount,
                )

        const isNoPayment = paymentAmount <= 0

        const complexEnabled = !isNoPayment && payMethodInfo.selectedRechargePayMethod && isComplex
        const shouldOnlyRecharge = payMethodInfo.selectedRechargePayMethod && !isComplex

        const payMethods = (function () {
            if (isNoPayment) {
                return []
            }
            return shouldOnlyRecharge ? [PayMethod.RECHARGE_POINT] : selected.payMethods
        })()

        const selectedPayMethod = isNoPayment || shouldOnlyRecharge ? PayMethod.NONE : payMethodInfo.selectedPayMethod

        return {
            status: (function () {
                if (isNoPayment) {
                    return PaymentMethodStatus.PAY_METHOD_NO_PAYMENT
                }

                return PaymentMethodStatus.NORMAL
            })(),
            tabStatus: (function (): Record<PaymentTabMethodType, PaymentMethodStatusType> {
                if (selectedCardPromotionGroup) {
                    return {
                        [PaymentTabMethod.RECHARGE]: PaymentMethodStatus.SELECTED_CARD_PROMOTION_EXIST,
                        [PaymentTabMethod.NORMAL]: PaymentMethodStatus.SELECTED_CARD_PROMOTION_EXIST,
                        [PaymentTabMethod.EASY_ACCOUNT]: PaymentMethodStatus.SELECTED_CARD_PROMOTION_EXIST,
                        [PaymentTabMethod.EASY_CARD]: PaymentMethodStatus.NORMAL,
                        [PaymentTabMethod.SAMSUNG_PAY]: PaymentMethodStatus.SELECTED_CARD_PROMOTION_EXIST,
                        [PaymentTabMethod.FAMILY_PAY]: PaymentMethodStatus.SELECTED_CARD_PROMOTION_EXIST,
                        [PaymentTabMethod.MOBILE]: PaymentMethodStatus.SELECTED_CARD_PROMOTION_EXIST,
                        [PaymentTabMethod.NONE]: PaymentMethodStatus.SELECTED_CARD_PROMOTION_EXIST,
                    }
                }
                return {
                    [PaymentTabMethod.RECHARGE]: PaymentMethodStatus.NORMAL,
                    [PaymentTabMethod.NORMAL]: PaymentMethodStatus.NORMAL,
                    [PaymentTabMethod.EASY_ACCOUNT]: PaymentMethodStatus.NORMAL,
                    [PaymentTabMethod.EASY_CARD]: PaymentMethodStatus.NORMAL,
                    [PaymentTabMethod.SAMSUNG_PAY]: PaymentMethodStatus.NORMAL,
                    [PaymentTabMethod.FAMILY_PAY]: PaymentMethodStatus.NORMAL,
                    [PaymentTabMethod.MOBILE]: PaymentMethodStatus.NORMAL,
                    [PaymentTabMethod.NONE]: PaymentMethodStatus.NORMAL,
                }
            })(),
            payMethods,
            tabMethods: payMethods.reduce<PaymentTabMethodType[]>((acc, payMethod) => {
                switch (payMethod) {
                    case PayMethod.CHARGE_POINT:
                    case PayMethod.RECHARGE_POINT:
                        acc.push(PaymentTabMethod.RECHARGE)
                        return acc
                    case PayMethod.CARD_EASY_PAY:
                        acc.push(PaymentTabMethod.EASY_CARD)
                        return acc
                    case PayMethod.BANK_EASY_PAY:
                        acc.push(PaymentTabMethod.EASY_ACCOUNT)
                        return acc
                    case PayMethod.CARD:
                    case PayMethod.MOBILE_EASY_PAY:
                    case PayMethod.MOBILE:
                    case PayMethod.SKIP:
                    case PayMethod.VIRTUAL_ACCOUNT:
                    case PayMethod.WELFARE_CARD:
                    case PayMethod.CULTURE_LAND:
                    case PayMethod.HAPPY_MONEY:
                    case PayMethod.MONEY_TOKEN:
                    case PayMethod.PAY_PAL:
                    case PayMethod.APPLE_PAY:
                        acc.push(PaymentTabMethod.NORMAL)
                        return acc
                    case PayMethod.SAMSUNG_PAY:
                        acc.push(PaymentTabMethod.SAMSUNG_PAY)
                        return acc
                    case PayMethod.FAMILY_PAY:
                        acc.push(PaymentTabMethod.FAMILY_PAY)
                        return acc
                    default:
                        return acc
                }
            }, []),
            complexEnabled,
            ...payMethodInfo,
            selectedPayMethod,
            /** 결제수단이 선택 되어야 하는지 여부 */
            isNeedSelectedPayMethod: selectedPayMethod === PayMethod.NONE && usingPayMethodAmount > 0,
        }
    },
)

/** 일반 결제수단 선택시 선택 되어야하는 일반 결제수단 */
export const nextNormalPayMethodSelector = createSelector(
    paymentSelector,
    paymentMethodInfoSelector,
    ({availableTypes, defaultNormalPayMethod}, {payMethods: currentPayMethods}): PayMethodType => {
        const currentPayMethod = currentPayMethods.find((pay) => NormalPayMethods.includes(pay))
        if (currentPayMethod) {
            return currentPayMethod
        }
        if (!isEmpty(availableTypes)) {
            /**
             * 일반결제 진입시 첫번째 일반결제수단이 아닌 최근 일반결제수단으로 선택
             * @ref https://oss.fin.navercorp.com/payOrder/plan/issues/1245
             **/
            if (defaultNormalPayMethod) {
                return defaultNormalPayMethod
            }
            const nextPayMethod = NormalPayMethods.find((payMethod) => availableTypes.includes(payMethod))
            if (nextPayMethod) {
                return nextPayMethod
            }
        }
        return PayMethod.NONE
    },
)

/** 선택된 결제수단 전체 사용불가 여부 */
export const selectedPayMethodServiceDisabledSelector = createSelector(
    payMethodInfoSelector,
    payMoneyDisabledSelector,
    cardEasyPayInfoSelector,
    cardSelector,
    bankEasyPayAccountInfoSelector,
    accountSelector,
    paymentAmountInfoSelectors,
    (
        {selectedRechargePayMethod, selectedPayMethod},
        {disabledReason},
        {naverPayService, selected},
        {cardEasyPay},
        {selected: selectedBank, payServiceInfo, maintenance},
        {bankEasyPay},
        {paymentAmount},
    ) => {
        /** 충전결제 / 페이머니 */
        if (selectedRechargePayMethod) {
            return !!disabledReason
        }
        switch (selectedPayMethod) {
            /** 간편카드 결제 */
            case PayMethod.CARD_EASY_PAY: {
                /** 네이버페이 사용가능 여부(점검 및 서킷 발동) */
                const isAvailableCardService = naverPayService.available
                /** 유효한 아이템 타입이 선택되었는지 */
                const isValidItemTypeSelected = selected.isValid
                /** 최대 결제가능액 초과하지 않았는지 */
                const isPaymentWithinLimit =
                    paymentAmount <= (cardEasyPay?.billing.maximumAmount ?? Number.MAX_SAFE_INTEGER)
                /** 선택된 카드 사용가능 여부 */
                const isAvailableCard =
                    isValidItemTypeSelected &&
                    selected.info?.payService.payable &&
                    selected.info.payService.supportedMerchant &&
                    isPaymentWithinLimit

                return [isAvailableCardService, isValidItemTypeSelected, isAvailableCard].some((bool) => !bool)
            }
            /** 간편계좌 결제 */
            case PayMethod.BANK_EASY_PAY: {
                /** 결제 서비스 사용가능 여부 */
                const isPayServiceAvailable = !payServiceInfo?.unavailableReason
                /** 은행사 전체 점검 여부 */
                const isAllBanksAvailable = !maintenance
                /** 선택된 계좌 사용가능 여부 */
                const isAvailableBank = selectedBank.payAvailable
                /** 최대 결제가능액 초과하지 않았는지 */
                const isPaymentWithinLimit =
                    paymentAmount <= (bankEasyPay.billing.maximumAmount ?? Number.MAX_SAFE_INTEGER)
                return [isPayServiceAvailable, isAllBanksAvailable, isAvailableBank, isPaymentWithinLimit].some(
                    (bool) => !bool,
                )
            }
            /** 전체 보기, 계좌/카드 추가 플레이트 선택 */
            case PayMethod.NONE: {
                return true
            }
            default:
                return false
        }
    },
)

/** 결제하기 버튼 활성화 여부 selector */
export const orderSubmitDisabledSelector = createSelector(
    paymentMethodInfoSelector,
    cardEasyPayInfoSelector,
    ({selectedPayMethod}, {naverPayService, selected}) =>
        !(selectedPayMethod === PayMethod.CARD_EASY_PAY
            ? // 네이버페이 사용가능 여부(점검 및 서킷 발동)
              naverPayService.available &&
              // 카드 원천사 결제가능 여부 (점검 및 서킷 발동)
              (selected.info?.payService.payable ?? true)
            : true),
)

export const paymentTabMethodInfoSelector = createSelector(
    payMethodAvailableSelector,
    familyPayStatementSelector,
    (
        {
            isRechargePointAvailable,
            isEasyPayBankAvailable,
            isEasyPayCardAvailable,
            isGeneralPaymentAvailable,
            isSamsungPayAvailable,
        },
        {isHidden},
    ) => {
        /** 결제수단 사용가능여부. 순서 변경 X */
        const availableMap: Record<Exclude<PaymentTabMethodType, 'MOBILE' | 'NONE'>, boolean> = {
            [PaymentTabMethod.RECHARGE]: isRechargePointAvailable,
            [PaymentTabMethod.EASY_ACCOUNT]: isEasyPayBankAvailable,
            [PaymentTabMethod.EASY_CARD]: isEasyPayCardAvailable,
            [PaymentTabMethod.NORMAL]: isGeneralPaymentAvailable,
            [PaymentTabMethod.SAMSUNG_PAY]: isSamsungPayAvailable,
            [PaymentTabMethod.FAMILY_PAY]: !isHidden,
        }

        /** 배열반환 */
        const tabList = Object.entries(availableMap)
            .filter(([, available]) => available)
            .map(([method]) => method)

        /** 사용가능한(결제수단 목록에 노출할) 결제수단 */
        return {
            availableTabList: tabList,
        }
    },
)

/** 결제수단 접기/펼치기 버튼 사용을 위한 최소 결제수단 갯수 */
const MINIMUM_PAYMENT_LIST_SIZE = 2

/** 결제수단 목록 접기 펼치기 버튼 사용가능 여부 */
export const paymentMethodsCollapsibleSelector = createSelector(
    paymentTabMethodInfoSelector,
    paymentSelector,
    pageSetInfoSelector,
    commonSelector,
    ({availableTabList}, {defaultPayMethod}, {isNpayBizCheckout}, {initOrderSheetDone}) => {
        return (
            /** 결제수단 목록이 최소 갯수 이상이고 */
            availableTabList.length >= MINIMUM_PAYMENT_LIST_SIZE &&
            /** 기본 결제수단이 설정되어있고 */
            defaultPayMethod !== PayMethod.NONE &&
            /** NpayBiz(외부주문서)가 아니고 */
            !isNpayBizCheckout &&
            /** 주문서 초기화가 완료되었을때 사용이 가능하다.
             * [desc] 주문형은 기본결제 설정 시, 이전 결제수단이 충전결제면 초기에 복합결제가 설정된 후 액션을 거쳐 단일결제수단으로 변경되는 플로우가 있기때문에, 해당 액션이 다 끝난 초기화 완료 시점에 사용가능을 반환한다. */
            initOrderSheetDone
        )
    },
)

/** 선택한 일반결제수단의 billing 정보 반환 */
export const generalPaymentInfoSelector = createSelector(
    paymentMethodInfoSelector,
    mobileSelector,
    voucherSelector,
    cardSelector,
    moneyTokenSelector,
    payPalSelector,
    ({selectedPayMethod}, mobileInfo, {cultureLand, happyMoney}, {creditCard, welfareCard}, moneyToken, payPal) => {
        switch (selectedPayMethod) {
            case PayMethod.MOBILE:
                return {
                    pgId: mobileInfo.mobileGeneralPay.billing.pgId,
                    authno: mobileInfo.mobileGeneralPay.phoneNumbers.replace(/\D+/g, ''),
                    pgCp: mobileInfo.mobileGeneralPay.billing.pgCp,
                }
            case PayMethod.MOBILE_EASY_PAY:
                return {
                    pgId: mobileInfo.mobileEasyPay.billing.pgId,
                    authno: mobileInfo.mobileEasyPay.phoneNumbers.replace(/\D+/g, ''),
                    pgCp: mobileInfo.mobileEasyPay.billing.pgCp,
                }
            case PayMethod.CULTURE_LAND:
                return {
                    pgId: cultureLand.billing.pgId,
                    tradMthdId: cultureLand.loginId,
                    pgCp: cultureLand.billing.pgCp,
                }
            case PayMethod.CARD:
                return {
                    corpCd: creditCard?.selected?.companyCode,
                    pgId: creditCard?.billing.pgId,
                    pgCp: creditCard?.billing.pgCp,
                }
            case PayMethod.HAPPY_MONEY:
                return {
                    pgId: happyMoney.billing.pgId,
                    pgCp: happyMoney.billing.pgCp,
                }
            case PayMethod.WELFARE_CARD:
                return {
                    pgId: welfareCard?.billing.pgId,
                    pgCp: welfareCard?.billing.pgCp,
                }
            case PayMethod.MONEY_TOKEN:
                return {
                    pgId: moneyToken?.billing.pgId,
                    pgCp: moneyToken?.billing.pgCp,
                }
            case PayMethod.PAY_PAL:
                return {pgId: payPal.billing?.pgId, pgCp: payPal.billing?.pgCp}
        }
        return null
    },
)

export const InterestInstallmentTypeMap: Record<
    InterestFreeInstallmentTypeType,
    'NONE' | 'NOT_FREE' | 'FREE' | 'PART_FREE'
> = {
    [InterestFreeInstallmentType.NONE]: 'NOT_FREE',
    [InterestFreeInstallmentType.ALL]: 'FREE',
    [InterestFreeInstallmentType.PART]: 'PART_FREE',
    // 실제로는 전달되지 않는 타입. 삭제예정.
    [InterestFreeInstallmentType.PRODUCT]: 'NOT_FREE',
} as const

/** 선택한 간편결제수단의 billing 정보 반환 */
export const easyPaymentInfoSelector = createSelector(
    cardSelector,
    cardEasyPayInfoSelector,
    selectedInstallmentInfoSelector,
    accountSelector,
    bankEasyPayAccountInfoSelector,
    chargePointSelector,
    rechargeAccountInfoSelector,
    paymentAmountInfoSelectors,
    payMethodInfoSelector,
    (
        {cardEasyPay: {billing: easyCardBilling}},
        {selected: selectedEasyCard},
        {selectedInstallmentInfo},
        {bankEasyPay: {billing: easyBankBilling}},
        {selected: selectedEasyBank},
        {recharging: {amount: rechargablePointAmount, processingType}},
        {selected: selectedChargeBank, accounts: chargeAccounts},
        {usingPayMethodAmount, totalUsingChargePoint},
        {selectedRechargePayMethod},
    ) => {
        const bankEasyPay = {
            bankAmount: usingPayMethodAmount,
            bankCode: selectedEasyBank.account?.bankCode ?? '',
            accountKey: selectedEasyBank.account?.accountKey ?? '',
            pgId: easyBankBilling.pgId,
            pgCp: easyBankBilling.pgCp,
        }

        const cardEasyPay = {
            cardAmount: usingPayMethodAmount,
            /** 카드사 코드 */
            cardCorpCode: selectedEasyCard.info?.companyCode ?? '',
            /** 카드사 id */
            cardCorpId: selectedEasyCard.info?.companyId ?? '',
            /** 카드 아이디 */
            id: selectedEasyCard.id ?? '',
            /** 할부기간 */
            installmentMonth: selectedEasyCard.installment,
            /** 선택된 할부 유형 */
            cardInstallmentInterestType: (() => {
                // 할부가 없는 경우
                if (selectedEasyCard.installment === '1') {
                    return 'NONE'
                }
                if (selectedInstallmentInfo) {
                    return InterestInstallmentTypeMap[selectedInstallmentInfo.freeType]
                }
                return 'NONE'
            })(),
            /** 카드사 포인트 사용여부 */
            usingCardCorpPoint: selectedEasyCard.usingCardCorpPoint ?? false,
            /** 빌링 pgid */
            pgId: easyCardBilling.pgId,
            /** 빌링 pgCp */
            pgCp: easyCardBilling.pgCp,
        }

        const isDeferredCharge = processingType === 'DEFERRED'
        // 충전이 나중에 일어나는 케이스는 현재 머니를 사용하는것이 목표가 아니므로 제외하고 제출한다
        const payMoney = {
            payMoneyAmount: isDeferredCharge ? 0 : totalUsingChargePoint,
            payMoneyTopUpAccountKey: '',
            payMoneyTopUpBankCode: '',
            payMoneyTopUpAmount: 0,
        }

        // 페이머니 충전 계좌 정보는 충전이 후에 이어지는 케이스이거나, 충전금액 있을 때에만 전달
        if ((isDeferredCharge && selectedRechargePayMethod) || rechargablePointAmount) {
            payMoney.payMoneyTopUpAmount = isDeferredCharge ? 0 : rechargablePointAmount
            payMoney.payMoneyTopUpBankCode = chargeAccounts[selectedChargeBank.index]?.bankCode ?? ''
            payMoney.payMoneyTopUpAccountKey = chargeAccounts[selectedChargeBank.index]?.accountKey ?? ''
        }

        return {
            bankEasyPay,
            cardEasyPay,
            payMoney,
        }
    },
)
